/**
 * SharedPagesDashboard - Manage All Shared Pages
 *
 * A dialog component that displays a table of all shares the user has created,
 * with filtering by status, actions menu, and view activity tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    IconButton,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Tooltip,
    alpha,
    useTheme,
    useMediaQuery,
    Switch,
    FormControlLabel,
    Checkbox,
    TextField,
    Divider,
    InputAdornment,
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    OpenInNew as OpenInNewIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    Block as RevokeIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Check as CheckIcon,
    Refresh as RefreshIcon,
    Schedule as ScheduleIcon,
    Warning as WarningIcon,
    Settings as SettingsIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isPast, parseISO, isValid } from 'date-fns';

/**
 * Safely parse a date value that could be:
 * - ISO string
 * - Firestore Timestamp (with _seconds and _nanoseconds)
 * - Date object
 * Returns null if invalid
 */
const safeParseDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's already a Date object
    if (dateValue instanceof Date) {
        return isValid(dateValue) ? dateValue : null;
    }

    // If it's a Firestore Timestamp (has _seconds or seconds property)
    if (dateValue._seconds !== undefined || dateValue.seconds !== undefined) {
        const seconds = dateValue._seconds ?? dateValue.seconds;
        const date = new Date(seconds * 1000);
        return isValid(date) ? date : null;
    }

    // If it's an ISO string
    if (typeof dateValue === 'string') {
        const date = parseISO(dateValue);
        return isValid(date) ? date : null;
    }

    // If it's a number (timestamp in milliseconds)
    if (typeof dateValue === 'number') {
        const date = new Date(dateValue);
        return isValid(date) ? date : null;
    }

    return null;
};
import {
    Collections as CollectionsIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/index.js';
import { listShares, revokeShare, deleteShare, getShareViews, getShareUrl, updateShare, createCollection } from '../services/shareService.js';
import { vortxColors } from '../core/theme.js';

// Status tab values
const STATUS_TABS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' },
];

// Get human-readable resource type
const getResourceTypeLabel = (share) => {
    const type = share.type || share.resource_type;
    const views = share.allowed_views || [];
    if (type === 'proposal' || views.includes('proposal')) return 'Proposal';
    switch (type) {
        case 'scenario':
            return 'Scenario';
        case 'scenario_comparison':
            return 'Comparison';
        case 'project':
            return 'Project';
        case 'project_collection':
            return 'Collection';
        default:
            return type || 'Unknown';
    }
};

// Get status from share data
const getShareStatus = (share) => {
    if (share.status === 'revoked') return 'revoked';
    const expiresDate = safeParseDate(share.expires_at);
    if (expiresDate && isPast(expiresDate)) return 'expired';
    return 'active';
};

// Get status chip color
const getStatusColor = (status) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'expired':
            return 'warning';
        case 'revoked':
            return 'error';
        default:
            return 'default';
    }
};

/**
 * View Activity Dialog - Shows who viewed a shared page
 */
function ViewActivityDialog({ open, onClose, shareId, shareTitle, currentUser }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [views, setViews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && shareId && currentUser) {
            loadViews();
        }
    }, [open, shareId, currentUser]);

    const loadViews = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getShareViews(shareId, currentUser);
            setViews(result.views || []);
        } catch (err) {
            console.error('Failed to load views:', err);
            setError(err.message || 'Failed to load view history');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    border: isMobile ? 'none' : `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                    pb: 2,
                }}
            >
                <Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: vortxColors.gold }}
                    >
                        View Activity
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {shareTitle || 'Shared Page'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: vortxColors.gold }} />
                    </Box>
                ) : views.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <VisibilityIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography color="text.secondary">
                            No one has viewed this share yet
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Viewed</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Downloads</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {views.map((view, index) => (
                                    <TableRow key={view.id || index} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {view.viewer_email || 'Anonymous'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const viewedDate = safeParseDate(view.viewed_at);
                                                return (
                                                    <Tooltip
                                                        title={viewedDate ? format(viewedDate, 'PPpp') : 'Unknown'}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            {viewedDate
                                                                ? formatDistanceToNow(viewedDate, { addSuffix: true })
                                                                : 'Unknown'}
                                                        </Typography>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {view.downloads && view.downloads.length > 0 ? (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {view.downloads.map((dl, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={dl.format?.toUpperCase() || 'FILE'}
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                height: 20,
                                                                bgcolor: alpha(vortxColors.teal, 0.1),
                                                                color: vortxColors.teal,
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">
                                                    None
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(vortxColors.teal, 0.2)}` }}>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Revoke Confirmation Dialog
 */
function RevokeConfirmDialog({ open, onClose, onConfirm, shareTitle, loading }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon sx={{ color: 'error.main' }} />
                Revoke Share Access
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    Are you sure you want to revoke access to "{shareTitle}"? This will immediately
                    prevent anyone from viewing this shared page.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    color="error"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Revoke Access'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Delete Confirmation Dialog
 */
function DeleteConfirmDialog({ open, onClose, onConfirm, shareTitle, loading }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon sx={{ color: 'error.main' }} />
                Permanently Delete Share
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    Are you sure you want to permanently delete "{shareTitle}"? This action cannot be undone
                    and all view history will be lost.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    color="error"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete Permanently'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Edit Settings Dialog - Allows editing share access settings
 */
function EditSettingsDialog({ open, onClose, share, onSave, loading }) {
    const [title, setTitle] = useState('');
    const [requireEmail, setRequireEmail] = useState(false);
    const [requirePassword, setRequirePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    // Initialize state when share changes
    useEffect(() => {
        if (share) {
            setTitle(share.title || '');
            setRequireEmail(share.access?.require_email || false);
            setRequirePassword(share.access?.require_password || false);
            setNewPassword('');
        }
    }, [share]);

    const handleSave = () => {
        const updates = {
            access: {
                require_email: requireEmail,
                require_password: requirePassword,
            },
        };

        // Include title if changed
        if (title !== (share?.title || '')) {
            updates.title = title;
        }

        // Only include password if user entered a new one
        if (requirePassword && newPassword) {
            updates.access.password = newPassword;
        }

        onSave(updates);
    };

    const titleChanged = title !== (share?.title || '');
    const hasChanges = share && (
        titleChanged ||
        requireEmail !== (share.access?.require_email || false) ||
        requirePassword !== (share.access?.require_password || false) ||
        (requirePassword && newPassword)
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon sx={{ color: vortxColors.gold }} />
                Edit Share Settings
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Changes take effect immediately for anyone with the link.
                </Typography>

                <TextField
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requireEmail}
                                onChange={(e) => setRequireEmail(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={500}>
                                    Require email
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Viewers must enter their email to access
                                </Typography>
                            </Box>
                        }
                    />

                    <Divider />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={requirePassword}
                                onChange={(e) => setRequirePassword(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={500}>
                                    Require password
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Viewers must enter a password to access
                                </Typography>
                            </Box>
                        }
                    />

                    {requirePassword && (
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={share?.access?.require_password ? 'Leave blank to keep current' : 'Enter password'}
                            helperText={
                                share?.access?.require_password
                                    ? 'Leave blank to keep current password'
                                    : 'Required when enabling password protection'
                            }
                            error={requirePassword && !share?.access?.require_password && !newPassword}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !hasChanges || (requirePassword && !share?.access?.require_password && !newPassword)}
                    sx={{
                        bgcolor: vortxColors.gold,
                        '&:hover': { bgcolor: '#9e7940' },
                    }}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * CreateCollectionDialog - Select existing shares to bundle into a collection
 */
function CreateCollectionDialog({ open, onClose, shares, currentUser, onCreated }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [title, setTitle] = useState('');
    const [labels, setLabels] = useState({});
    const [requirePassword, setRequirePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [requireEmail, setRequireEmail] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [createdUrl, setCreatedUrl] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(false);

    // Only show active proposal shares as candidates
    const candidates = shares.filter(
        (s) => getShareStatus(s) === 'active' && s.type !== 'project_collection'
    );

    const handleToggle = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleLabelChange = (id, value) => {
        setLabels((prev) => ({ ...prev, [id]: value }));
    };

    const handleCreate = async () => {
        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        if (selectedIds.length < 2) {
            setError('Select at least 2 shares');
            return;
        }
        if (requirePassword && !password) {
            setError('Password is required when password protection is enabled');
            return;
        }

        setCreating(true);
        setError(null);

        try {
            const childLabels = selectedIds.map((id) => labels[id] || '');
            const payload = {
                title: title.trim(),
                child_share_ids: selectedIds,
                child_labels: childLabels,
            };
            if (requirePassword) {
                payload.require_password = true;
                payload.password = password;
            }
            if (requireEmail) {
                payload.require_email = true;
            }
            const result = await createCollection(payload, currentUser);
            const url = getShareUrl(result.id, 'collection');
            setCreatedUrl(url);
            if (onCreated) onCreated();
        } catch (err) {
            console.error('Failed to create collection:', err);
            setError(err.message || 'Failed to create collection');
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = async () => {
        if (!createdUrl) return;
        try {
            await navigator.clipboard.writeText(createdUrl);
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleClose = () => {
        setSelectedIds([]);
        setTitle('');
        setLabels({});
        setRequirePassword(false);
        setPassword('');
        setShowPassword(false);
        setRequireEmail(false);
        setCreating(false);
        setError(null);
        setCreatedUrl(null);
        setCopiedUrl(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CollectionsIcon sx={{ color: vortxColors.gold }} />
                {createdUrl ? 'Collection Created' : 'New Collection'}
            </DialogTitle>
            <DialogContent>
                {createdUrl ? (
                    <Box sx={{ py: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Collection created successfully!
                        </Alert>
                        <TextField
                            fullWidth
                            size="small"
                            value={createdUrl}
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 1 }}
                        />
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={copiedUrl ? <CheckIcon /> : <CopyIcon />}
                            onClick={handleCopy}
                            sx={{
                                bgcolor: copiedUrl ? 'success.main' : vortxColors.gold,
                                '&:hover': { bgcolor: copiedUrl ? 'success.dark' : '#9e7940' },
                            }}
                        >
                            {copiedUrl ? 'Copied!' : 'Copy Link'}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Bundle multiple shared pages into a single link with project tabs.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            label="Collection Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g., NEO AV's 2026 — All Sites"
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                            Select shares to include ({selectedIds.length} selected)
                        </Typography>

                        {candidates.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No active shares available. Create proposal shares first.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" sx={{ fontWeight: 600 }} />
                                            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Tab Label</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {candidates.map((share) => {
                                            const isSelected = selectedIds.includes(share.id);
                                            return (
                                                <TableRow
                                                    key={share.id}
                                                    hover
                                                    onClick={() => handleToggle(share.id)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Switch
                                                            checked={isSelected}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                            {share.title || 'Untitled'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        {isSelected && (
                                                            <TextField
                                                                size="small"
                                                                variant="standard"
                                                                placeholder={share.title || 'Tab label'}
                                                                value={labels[share.id] || ''}
                                                                onChange={(e) => handleLabelChange(share.id, e.target.value)}
                                                                sx={{ width: 120 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Access Controls */}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                            Access Controls
                        </Typography>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={requirePassword}
                                    onChange={(e) => {
                                        setRequirePassword(e.target.checked);
                                        if (!e.target.checked) setPassword('');
                                    }}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2">Require password</Typography>
                                </Box>
                            }
                        />

                        {requirePassword && (
                            <TextField
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                size="small"
                                sx={{ mt: 1, mb: 1, ml: 4 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={requireEmail}
                                    onChange={(e) => setRequireEmail(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2">Require email</Typography>
                                </Box>
                            }
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={creating}>
                    {createdUrl ? 'Close' : 'Cancel'}
                </Button>
                {!createdUrl && (
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        disabled={creating || selectedIds.length < 2 || !title.trim() || (requirePassword && !password)}
                        sx={{
                            bgcolor: vortxColors.gold,
                            '&:hover': { bgcolor: '#9e7940' },
                        }}
                    >
                        {creating ? <CircularProgress size={20} color="inherit" /> : 'Create Collection'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

/**
 * SharedPagesDashboard Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog closes
 */
export default function SharedPagesDashboard({ open, onClose }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { currentUser } = useAuth();

    // State
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    // Menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedShare, setSelectedShare] = useState(null);

    // View activity dialog state
    const [viewsDialogOpen, setViewsDialogOpen] = useState(false);
    const [viewsShare, setViewsShare] = useState(null);

    // Edit settings dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // Revoke dialog state
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [revokeLoading, setRevokeLoading] = useState(false);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Copy state
    const [copiedId, setCopiedId] = useState(null);

    // Collection dialog state
    const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);

    // Load shares when dialog opens or filter changes
    const loadShares = useCallback(async () => {
        if (!currentUser) return;

        setLoading(true);
        setError(null);

        try {
            const filterValue = statusFilter === 'all' ? null : statusFilter;
            const result = await listShares(currentUser, filterValue);
            setShares(result.shares || []);
        } catch (err) {
            console.error('Failed to load shares:', err);
            setError(err.message || 'Failed to load shared pages');
        } finally {
            setLoading(false);
        }
    }, [currentUser, statusFilter]);

    useEffect(() => {
        if (open && currentUser) {
            loadShares();
        }
    }, [open, currentUser, statusFilter, loadShares]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setMenuAnchor(null);
            setSelectedShare(null);
            setCopiedId(null);
        }
    }, [open]);

    // Filter shares client-side if needed (server may return all)
    const filteredShares = shares.filter((share) => {
        if (statusFilter === 'all') return true;
        return getShareStatus(share) === statusFilter;
    });

    // Handle menu open
    const handleMenuOpen = (event, share) => {
        setMenuAnchor(event.currentTarget);
        setSelectedShare(share);
    };

    // Handle menu close
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    // Get share type for URL
    const getShareTypeForUrl = (share) => {
        if (share.type === 'project_collection') return 'collection';
        if (share.type === 'proposal') return 'proposal';
        if (share.type === 'scenario_comparison') return 'comparison';
        return 'scenario';
    };

    // Copy link
    const handleCopyLink = async (share) => {
        const shareType = getShareTypeForUrl(share);
        const url = getShareUrl(share.id, shareType);

        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(share.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
        handleMenuClose();
    };

    // View page in new tab
    const handleViewPage = (share) => {
        const shareType = getShareTypeForUrl(share);
        const url = getShareUrl(share.id, shareType);
        window.open(url, '_blank');
        handleMenuClose();
    };

    // View activity
    const handleViewActivity = (share) => {
        setViewsShare(share);
        setViewsDialogOpen(true);
        handleMenuClose();
    };

    // Revoke share
    const handleRevokeClick = (share) => {
        setRevokeDialogOpen(true);
        handleMenuClose();
    };

    const handleRevokeConfirm = async () => {
        if (!selectedShare || !currentUser) return;

        setRevokeLoading(true);
        try {
            await revokeShare(selectedShare.id, currentUser);
            await loadShares(); // Refresh the list
            setRevokeDialogOpen(false);
            setSelectedShare(null);
        } catch (err) {
            console.error('Failed to revoke share:', err);
            setError(err.message || 'Failed to revoke share');
        } finally {
            setRevokeLoading(false);
        }
    };

    // Delete share
    const handleDeleteClick = (share) => {
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = async () => {
        if (!selectedShare || !currentUser) return;

        setDeleteLoading(true);
        try {
            await deleteShare(selectedShare.id, currentUser);
            await loadShares(); // Refresh the list
            setDeleteDialogOpen(false);
            setSelectedShare(null);
        } catch (err) {
            console.error('Failed to delete share:', err);
            setError(err.message || 'Failed to delete share');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Edit settings
    const handleEditClick = () => {
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleEditSave = async (updates) => {
        if (!selectedShare || !currentUser) return;

        setEditLoading(true);
        try {
            await updateShare(selectedShare.id, updates, currentUser);
            await loadShares(); // Refresh the list
            setEditDialogOpen(false);
        } catch (err) {
            console.error('Failed to update share:', err);
            setError(err.message || 'Failed to update share settings');
        } finally {
            setEditLoading(false);
        }
    };

    // Format expiration date
    const formatExpiration = (expiresAt) => {
        if (!expiresAt) return 'Never';
        const date = safeParseDate(expiresAt);
        if (!date) return 'Unknown';
        if (isPast(date)) return 'Expired';
        return format(date, 'MMM d, yyyy');
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 2,
                        border: isMobile ? 'none' : `1px solid ${alpha(vortxColors.gold, 0.2)}`,
                        minHeight: isMobile ? '100%' : '60vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                        pb: 2,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: vortxColors.gold }}
                        >
                            Shared Pages
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Manage your shared scenarios and comparisons
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            size="small"
                            startIcon={<CollectionsIcon />}
                            onClick={() => setCollectionDialogOpen(true)}
                            sx={{
                                textTransform: 'none',
                                color: vortxColors.gold,
                                borderColor: alpha(vortxColors.gold, 0.5),
                                '&:hover': { borderColor: vortxColors.gold, bgcolor: alpha(vortxColors.gold, 0.08) },
                            }}
                            variant="outlined"
                        >
                            New Collection
                        </Button>
                        <Tooltip title="Refresh">
                            <IconButton onClick={loadShares} size="small" disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* Status Filter Tabs */}
                <Box sx={{ borderBottom: `1px solid ${alpha(vortxColors.teal, 0.1)}`, px: 2 }}>
                    <Tabs
                        value={statusFilter}
                        onChange={(e, newValue) => setStatusFilter(newValue)}
                        variant={isMobile ? 'scrollable' : 'standard'}
                        scrollButtons={isMobile ? 'auto' : false}
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                minWidth: isMobile ? 'auto' : 100,
                            },
                            '& .Mui-selected': {
                                color: vortxColors.gold,
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: vortxColors.gold,
                            },
                        }}
                    >
                        {STATUS_TABS.map((tab) => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                </Box>

                <DialogContent sx={{ pt: 2, pb: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: vortxColors.gold }} />
                        </Box>
                    ) : filteredShares.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                No shared pages
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                {statusFilter === 'all'
                                    ? 'Share a scenario comparison to see it here'
                                    : `No ${statusFilter} shares found`}
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                        {!isMobile && (
                                            <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                                        )}
                                        <TableCell sx={{ fontWeight: 600 }} align="center">
                                            Views
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredShares.map((share) => {
                                        const status = getShareStatus(share);
                                        const isActive = status === 'active';

                                        return (
                                            <TableRow key={share.id} hover>
                                                <TableCell>
                                                    <Chip
                                                        label={getResourceTypeLabel(share)}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 22,
                                                            bgcolor: alpha(vortxColors.teal, 0.1),
                                                            color: vortxColors.teal,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={500}
                                                        sx={{
                                                            maxWidth: isTablet ? 150 : 300,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {share.title || 'Untitled'}
                                                    </Typography>
                                                    {isMobile && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            {formatExpiration(share.expires_at)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {formatExpiration(share.expires_at)}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell align="center">
                                                    <Tooltip title="View activity">
                                                        <Button
                                                            size="small"
                                                            onClick={() => handleViewActivity(share)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                px: 1,
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: vortxColors.gold,
                                                                },
                                                            }}
                                                        >
                                                            {share.view_count || 0}
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                                                        size="small"
                                                        color={getStatusColor(status)}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 22,
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                        {!isMobile && (
                                                            <Tooltip title={copiedId === share.id ? 'Copied!' : 'Copy link'}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleCopyLink(share)}
                                                                    sx={{
                                                                        color: copiedId === share.id
                                                                            ? 'success.main'
                                                                            : 'text.secondary',
                                                                    }}
                                                                >
                                                                    {copiedId === share.id ? (
                                                                        <CheckIcon fontSize="small" />
                                                                    ) : (
                                                                        <CopyIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {!isMobile && isActive && (
                                                            <Tooltip title="Edit settings">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setSelectedShare(share);
                                                                        setEditDialogOpen(true);
                                                                    }}
                                                                    sx={{ color: 'text.secondary' }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleMenuOpen(e, share)}
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                        {filteredShares.length} shared page{filteredShares.length !== 1 ? 's' : ''}
                    </Typography>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Actions Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => selectedShare && handleCopyLink(selectedShare)}>
                    <ListItemIcon>
                        <CopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy Link</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedShare && handleViewPage(selectedShare)}>
                    <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Page</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => selectedShare && handleViewActivity(selectedShare)}>
                    <ListItemIcon>
                        <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Activity</ListItemText>
                </MenuItem>
                {selectedShare && getShareStatus(selectedShare) === 'active' && (
                    <MenuItem onClick={handleEditClick}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit Settings</ListItemText>
                    </MenuItem>
                )}
                {selectedShare && getShareStatus(selectedShare) === 'active' && (
                    <MenuItem
                        onClick={() => handleRevokeClick(selectedShare)}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <RevokeIcon fontSize="small" sx={{ color: 'error.main' }} />
                        </ListItemIcon>
                        <ListItemText>Revoke Access</ListItemText>
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => handleDeleteClick(selectedShare)}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText>Delete Permanently</ListItemText>
                </MenuItem>
            </Menu>

            {/* View Activity Dialog */}
            <ViewActivityDialog
                open={viewsDialogOpen}
                onClose={() => {
                    setViewsDialogOpen(false);
                    setViewsShare(null);
                }}
                shareId={viewsShare?.id}
                shareTitle={viewsShare?.title}
                currentUser={currentUser}
            />

            {/* Revoke Confirmation Dialog */}
            <RevokeConfirmDialog
                open={revokeDialogOpen}
                onClose={() => setRevokeDialogOpen(false)}
                onConfirm={handleRevokeConfirm}
                shareTitle={selectedShare?.title || 'this share'}
                loading={revokeLoading}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                shareTitle={selectedShare?.title || 'this share'}
                loading={deleteLoading}
            />

            {/* Edit Settings Dialog */}
            <EditSettingsDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                share={selectedShare}
                onSave={handleEditSave}
                loading={editLoading}
            />

            {/* Create Collection Dialog */}
            <CreateCollectionDialog
                open={collectionDialogOpen}
                onClose={() => setCollectionDialogOpen(false)}
                shares={shares}
                currentUser={currentUser}
                onCreated={loadShares}
            />
        </>
    );
}
